import React, { useEffect, useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const successStories = [{
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oaqBiuQGPBgGxL6VZu6J2T/4.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3PLRUth4LRxMnYX3wqPE3R/6.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eKtPHrbGki6THkqDXxi6PY/7.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3wrPXbf4Gf2U4w91eRbtH3/14.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pkVgupBajKH15WERVgX61L/1.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5hH1KAowDmBEhQWL38s8qG/9.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/2FRw3nCAtK7Bi1hjWep4EM/10.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3TCTiJULibxqZ94B9VGJo7/12.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9kBsFo9TEhCmtajmqz6YLA/5.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/nmmuv4wkGEfDbHRcbtQraX/8.jpg',
  rating: 5
}, {
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/58w3ffSxadHPe32fXzxsgP/15.jpg',
  rating: 5
}];
const SuccessStories = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayCount, setDisplayCount] = useState(4);
  const [animatedStars, setAnimatedStars] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Update display count based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setDisplayCount(4); // 4 cards on large screens
      } else if (window.innerWidth >= 768) {
        setDisplayCount(3); // 3 cards on medium screens
      } else if (window.innerWidth >= 640) {
        setDisplayCount(2); // 2 cards on small screens
      } else {
        setDisplayCount(1); // 1 card on extra small screens
      }
    };
    // Set initial value
    handleResize();
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // Auto-rotate carousel
  useEffect(() => {
    // Start auto-rotation
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % (successStories.length - (displayCount - 1) || 1));
    }, 5000); // Slow rotation - 5 seconds per slide
    // Clean up on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [displayCount]);
  // Animate stars on component mount
  useEffect(() => {
    setTimeout(() => {
      setAnimatedStars(true);
    }, 500);
  }, []);
  const renderStars = (count: number) => {
    return Array(count).fill(null).map((_, index) => <span key={index} className={`text-yellow-500 transition-opacity duration-700 ease-in-out ${animatedStars ? 'opacity-100' : 'opacity-0'}`} style={{
      transitionDelay: `${index * 150}ms`
    }} aria-hidden="true" data-id="element-298">
          ⭐
        </span>);
  };
  const nextSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setCurrentIndex(prevIndex => (prevIndex + 1) % (successStories.length - (displayCount - 1) || 1));
    // Restart auto-rotation after manual navigation
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % (successStories.length - (displayCount - 1) || 1));
    }, 5000);
  };
  const prevSlide = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setCurrentIndex(prevIndex => prevIndex === 0 ? successStories.length - displayCount : prevIndex - 1);
    // Restart auto-rotation after manual navigation
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % (successStories.length - (displayCount - 1) || 1));
    }, 5000);
  };
  return <section className="py-8 bg-gradient-to-b from-gray-50 to-blue-50" data-id="element-299">
      <div className="container mx-auto px-3" data-id="element-300">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-1" data-id="element-301">
          Recent Passes 🎓
        </h2>
        <p className="text-gray-600 text-center mb-6" data-id="element-302">
          Our students share their proudest moments, will you be next?
        </p>
        {/* Carousel */}
        <div className="relative" data-id="element-303">
          <div className="overflow-hidden" data-id="element-304">
            <div className="flex transition-transform duration-1000 ease-in-out" style={{
            transform: `translateX(-${currentIndex * (100 / displayCount)}%)`
          }} data-id="element-305">
              {successStories.map((story, index) => <div key={index} className={`w-full sm:w-1/2 md:w-1/3 lg:w-1/4 flex-shrink-0 px-2 transition-transform duration-300`} data-id="element-306">
                  <div className="bg-white rounded-lg shadow-md overflow-hidden h-full group hover:shadow-lg transition-all duration-300" data-id="element-307">
                    <div className="relative overflow-hidden" data-id="element-308">
                      <div className="aspect-w-3 aspect-h-4 overflow-hidden" data-id="element-309">
                        <img src={story.image} alt="Learner passing driving test in Northampton with certificate" className="w-full h-72 object-cover object-center rounded-t-lg group-hover:scale-105 group-hover:brightness-105 transition-all duration-500" loading="lazy" data-id="element-310" />
                      </div>
                      <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold py-1 px-3 rounded-full shadow-md transform -rotate-12" data-id="element-311">
                        Passed!
                      </div>
                    </div>
                    <div className="p-3 text-center" data-id="element-312">
                      <div className="flex justify-center" data-id="element-313">
                        {renderStars(story.rating)}
                        <span className="sr-only" data-id="element-314">5 star rating</span>
                      </div>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Navigation buttons */}
          <button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md text-gray-800 hover:bg-gray-100 z-10 -ml-4" aria-label="Previous slide" data-id="element-315">
            <ChevronLeft size={24} aria-hidden="true" data-id="element-316" />
          </button>
          <button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 bg-white p-2 rounded-full shadow-md text-gray-800 hover:bg-gray-100 z-10 -mr-4" aria-label="Next slide" data-id="element-317">
            <ChevronRight size={24} aria-hidden="true" data-id="element-318" />
          </button>
          {/* Indicators */}
          <div className="flex justify-center mt-4" data-id="element-319">
            {Array.from({
            length: successStories.length - (displayCount - 1) || 1
          }).map((_, index) => <button key={index} onClick={() => {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
            }
            setCurrentIndex(index);
            // Restart auto-rotation after manual navigation
            intervalRef.current = setInterval(() => {
              setCurrentIndex(prevIndex => (prevIndex + 1) % (successStories.length - (displayCount - 1) || 1));
            }, 5000);
          }} className={`w-2.5 h-2.5 rounded-full mx-1 ${currentIndex === index ? 'bg-burgundy-600' : 'bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-320" />)}
          </div>
        </div>
        {/* CTA Button */}
        <div className="text-center mt-8" data-id="element-321">
          <a href="https://www.totaldrive.app/a/enquiry_form.php?17584722727972" target="_blank" rel="noopener noreferrer" className="bg-burgundy-600 hover:bg-burgundy-700 text-white px-6 py-3 rounded-md font-medium inline-block transition-colors shadow-md" aria-label="Book your driving lessons in Northampton" data-id="element-322">
            Book Your Lesson – Be Our Next Success Story
          </a>
        </div>
      </div>
    </section>;
};
export default SuccessStories;